import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, navigate } from 'gatsby';
import { Index } from 'elasticlunr';
import ReactHtmlParser from 'react-html-parser';
import ReactDOMServer from 'react-dom/server';
import clip from 'text-clipper';
import { GenTeaser, Icon } from '@massds/mayflower-react';

import Pager from '../Pager';
import NoResults from '../NoResults';
import DangerousWrapper from '../DangerousWrapper';
import './index.scss';

class SearchResults extends Component {
  constructor(props) {
    super(props);
    const { query = '', updateResults } = this.props;
    this.indexQueried = false;
    if (query.length) {
      updateResults(this.queryIndex(query));
    }
    this.originalQuery = (query && query.length > 0) ? query : '';
  }

  componentDidUpdate(prevProps) {
    const { query = '', updateResults } = this.props;
    if (query.length && prevProps.query !== query) {
      updateResults(this.queryIndex(query));
    }
  }

  getOrCreateIndex = () => {
    if (this.index) {
      return this.index;
    }
    const { searchIndex } = this.props;
    this.index = Index.load(searchIndex);
    return this.index;
  };

  pagerNavigate = (page) => {
    const { query } = this.props;
    const parsedPage = parseInt(page, 10);
    navigate(`/search?query=${query}&page=${parsedPage}`);
  };

  handlePageClick = (e) => {
    // For IE10 compatibility
    const page = e.nativeEvent.target.getAttribute('data-page');
    this.pagerNavigate(page);
  };

  handleNextClick = () => {
    const { page } = this.props;
    const nextPage = parseInt(page, 10) + 1;
    this.pagerNavigate(nextPage);
  };

  handlePrevClick = () => {
    const { page } = this.props;
    const prevPage = parseInt(page, 10) - 1;
    this.pagerNavigate(prevPage);
  };

  queryIndex = (query) => {
    const index = this.getOrCreateIndex();
    const options = {
      expand: true
    };
    this.indexQueried = true;
    return index
      .search(query, options)
      // Map over each ID and return the full document
      .map(({ ref }) => index.documentStore.getDoc(ref));
  };

  render() {
    const { page, results } = this.props;
    let startPage = parseInt(page, 10);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(startPage)) {
      startPage = 1;
    }
    const start = ((startPage - 1) * 10);
    const end = start + 10;
    // whether or not all results have no title
    const resultsNoTitle = results.every((result) => !result.title);
    // if there is one or more results, and not all of the results have no title
    if (results.length > 0 && !resultsNoTitle) {
      return(
        <Fragment>
          {results.slice(start, end).map((curPage) => {
            const {
              org = '', title, id, slug, summary
            } = curPage;
            // only render the result if title exist
            if (title) {
              const path = (slug && slug !== '/' && slug.endsWith('/')) ? slug.replace(/\/$/, '') : slug;
              const content = curPage.content && JSON.parse(curPage.content);
              const type = curPage.type && curPage.type.replace(/Json/g, '').replace(/([A-Z])/g, ' $1');
              let description;
              if (summary) {
                description = ReactDOMServer.renderToStaticMarkup(ReactHtmlParser(summary));
              } else if (content) {
                if (content.summary) {
                  description = ReactDOMServer.renderToStaticMarkup(ReactHtmlParser(content.summary));
                } else if (content.content) {
                  description = ReactDOMServer.renderToStaticMarkup(ReactHtmlParser(content.content));
                } else if (content.description) {
                  description = ReactDOMServer.renderToStaticMarkup(ReactHtmlParser(content.description));
                }
              }
              return(
                <GenTeaser key={id}>
                  <GenTeaser.Details>
                    {type && (<GenTeaser.Eyebrow eyebrow={type} />)}
                    <GenTeaser.Title title={{ icon: null }}>
                      <Link to={path}>{title}</Link>
                      &nbsp;
                      <Icon name="arrow" svgWidth={13.2} svgHeight={13.2} />
                    </GenTeaser.Title>
                    {org && (
                      <GenTeaser.Emphasis>
                        <GenTeaser.Orgs orgs={org} />
                      </GenTeaser.Emphasis>
                    )}
                    {description && (
                      <GenTeaser.Description>
                        <DangerousWrapper rawHtml={clip(description, 260, { html: true, maxLines: 2 })} htmlTag="div" />
                      </GenTeaser.Description>
                    )}
                  </GenTeaser.Details>
                </GenTeaser>
              );
            }
            return null;
          })}
          <Pager
            key={`Pager.${this.originalQuery}.${page}`}
            page={page}
            totalResults={parseInt(results.length, 10)}
            handleNextClick={this.handleNextClick}
            handlePrevClick={this.handlePrevClick}
            handlePageClick={this.handlePageClick}
          />
        </Fragment>
      );
    }
    // If no results or all results have no title render NoResults
    if (this.indexQueried && (results.length === 0 || resultsNoTitle)) {
      return<NoResults query={this.originalQuery} />;
    }
    return null;
  }
}

SearchResults.propTypes = {
  // The query string past from the url query param or user input
  query: PropTypes.string,
  // Current page index number of the search results (based on 10 results per page)
  page: PropTypes.number,
  // Function passed from search.js setting the results array to state to render new search results
  updateResults: PropTypes.func,
  // The results array to state to render new search results
  results: PropTypes.object,
  // The search index object returned from @gatsby-contrib/gatsby-plugin-elasticlunr-search based on config options
  /* graphql query:
    query SearchPageQuery {
      siteSearchIndex {
        index
      }
    }
  */
  searchIndex: PropTypes.object
};

export default SearchResults;
